import type { CityConfig } from "../city-template/types";

export const hopkinsvilleConfig: CityConfig = {
  city: "Hopkinsville",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "hopkinsville",
  route: "/cities/hopkinsville",
  title: "Emergency Passport Hopkinsville, KY | Fast Passport Renewal Services",
  metaDescription: "Emergency passport and expedited passport services in Hopkinsville, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Christian County and Fort Campbell travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Hopkinsville, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Hopkinsville, Fort Campbell, and Christian County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Hopkinsville%20Kentucky%20downtown%20at%20golden%20hour%20with%20historic%20Christian%20County%20courthouse%2C%20charming%20small%20city%20atmosphere%2C%20warm%20western%20Kentucky%20sunset%20light%2C%20tree-lined%20Main%20Street%20with%20historic%20brick%20buildings%2C%20clean%20modern%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20vibrant%20Christian%20County%20scene%20with%20rolling%20green%20farmland%20in%20the%20background%2C%20I-24%20corridor%20view&width=1800&height=700&seq=hopkinsville-hero-001&orientation=landscape",
  heroImageAlt: "Hopkinsville Kentucky downtown at golden hour with historic Christian County courthouse and charming Main Street",
  geoPosition: "36.8656;-87.4886",
  geoPlacename: "Hopkinsville, Kentucky",
  regionalAgency: {
    name: "Atlanta Passport Agency",
    address: "230 Peachtree St NW, Suite 1000, Atlanta, GA 30308",
    distance: "Located in downtown Atlanta — approximately 320 miles from Hopkinsville via I-24",
  },
  nearbyCitiesZips: [
    { name: "Hopkinsville", zips: "42240, 42241" },
    { name: "Oak Grove", zips: "42262" },
    { name: "Fort Campbell", zips: "42223" },
    { name: "Cadiz", zips: "42211" },
    { name: "Eddyville", zips: "42038" },
    { name: "Princeton", zips: "42445" },
    { name: "Clarksville (TN)", zips: "37040–37044" },
    { name: "Russellville", zips: "42276" },
    { name: "Elkton", zips: "42220" },
    { name: "Pembroke", zips: "42266" },
    { name: "LaFayette", zips: "42254" },
    { name: "Fairview", zips: "42221" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Christian County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Louisville", route: "/cities/louisville" },
    { name: "Fast passport renewal in Clarksville", route: "/clarksville" },
    { name: "Emergency passport help in Bowling Green", route: "/cities/bowling-green" },
    { name: "Urgent passport services in Nashville", route: "/nashville" },
    { name: "Passport courier services near Fort Campbell", route: "/fort-campbell" },
    { name: "Same-day passport help in Paducah", route: "/cities/paducah" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Hopkinsville remotely via secure courier. We do not have a walk-in office in Hopkinsville — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Christian County and Fort Campbell travelers.",
  fpcOfficeDistance: "Serving all of Christian County and the Fort Campbell area",
  serviceAreaIntro: "From downtown Hopkinsville's historic district to the Fort Campbell military installation, from the Oak Grove suburbs to the surrounding Christian County farmland — Fast Passport Center serves the entire Hopkinsville community. We regularly help military families, veterans, and civilian travelers from Oak Grove, Cadiz, and Clarksville get their passports fast.",
  nearbyCitiesIntro: "We serve all Hopkinsville neighborhoods and nearby cities including Oak Grove, Fort Campbell, Cadiz, Eddyville, Princeton, Clarksville (TN), Russellville, Elkton, Pembroke, LaFayette, and Fairview. If you are in Christian County, the Fort Campbell area, or the Clarksville metro, we have you covered.",
  howItWorksIntro: "Most Hopkinsville travelers complete everything online and ship documents overnight. No need to drive to Atlanta or wait in line at the regional agency.",
  mistakesIntro: "Hopkinsville travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Hopkinsville and Christian County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Hopkinsville? Here are the most common ones we hear from Christian County and Fort Campbell travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Christian County and Fort Campbell international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Sgt. James K.",
      location: "Fort Campbell, KY",
      rating: 5,
      text: "Emergency deployment leave to visit family in Germany. Fast Passport Center got me a passport in 24 hours. As a soldier, I needed reliability and they delivered.",
      service: "Emergency Passport",
    },
    {
      name: "Rachel M.",
      location: "Hopkinsville, KY",
      rating: 5,
      text: "Disney cruise for my daughter's 10th birthday. My passport was expired. They got me a renewal in 3 days and we didn't miss the ship in Port Canaveral.",
      service: "Passport Renewal",
    },
    {
      name: "Dr. Lisa P.",
      location: "Clarksville, TN",
      rating: 5,
      text: "Medical conference in Tokyo. They got me a passport in 4 days. The dedicated agent walked me through every step. Highly professional service.",
      service: "Emergency Passport",
    },
    {
      name: "Mark T.",
      location: "Oak Grove, KY",
      rating: 5,
      text: "Family emergency in Ireland. They got me a replacement passport in 24 hours. I was able to say goodbye to my grandmother. Thank you from the bottom of my heart.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "hopkinsville-1",
      name: "Hopkinsville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "110 S Main St",
      city: "Hopkinsville",
      state: "KY",
      zip: "42240",
      phone: "(270) 885-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Hopkinsville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Christian County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "hopkinsville-2",
      name: "Oak Grove Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1701 W Fort Campbell Blvd",
      city: "Oak Grove",
      state: "KY",
      zip: "42262",
      phone: "(270) 439-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Fort Campbell Gate",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Fort Campbell military families and Oak Grove residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "hopkinsville-3",
      name: "Clarksville Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2031 Wilma Rudolph Blvd",
      city: "Clarksville",
      state: "TN",
      zip: "37040",
      phone: "(931) 647-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Clarksville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for the Clarksville metro and Tennessee side. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "hopkinsville-4",
      name: "Cadiz Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "131 S Main St",
      city: "Cadiz",
      state: "KY",
      zip: "42211",
      phone: "(270) 522-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Cadiz",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Trigg County and western Christian County. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "hopkinsville-5",
      name: "Russellville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "106 S Main St",
      city: "Russellville",
      state: "KY",
      zip: "42276",
      phone: "(270) 726-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Russellville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Logan County and eastern Christian County. Call for appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "hopkinsville-6",
      name: "Christian County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "511 S Main St",
      city: "Hopkinsville",
      state: "KY",
      zip: "42240",
      phone: "(270) 887-4105",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.2 miles from Christian County Courthouse",
      website: "https://www.christiancountyky.gov/",
      websiteLabel: "Christian County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5 hour drive to Atlanta", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Christian County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Christian County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Hopkinsville applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Atlanta regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Christian County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Christian County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Hopkinsville." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Hopkinsville address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Hopkinsville before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Hopkinsville for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 5 hour drive to Atlanta." },
    { q: "How fast can I renew my passport in Hopkinsville?", a: "Fast Passport Center offers fast passport renewal in Hopkinsville in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Hopkinsville in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Hopkinsville?", a: "Same-day passport appointments are only available at the Atlanta Regional Passport Agency — a 5 hour drive from Hopkinsville — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Christian County residents." },
    { q: "Can I expedite a child passport in Hopkinsville?", a: "Yes. Fast Passport Center expedites child passports in Hopkinsville for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Christian County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Hopkinsville with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Hopkinsville?", a: "With Fast Passport Center's emergency courier service, Hopkinsville travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Atlanta Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Hopkinsville?", a: "Popular options include the Hopkinsville Post Office (42240), Oak Grove Post Office (42262), Clarksville Main Post Office (TN), Cadiz Post Office (42211), Russellville Post Office (42276), and Christian County Clerk (42240). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Hopkinsville?", a: "Same-day passports are only available at the Atlanta Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 5 hour drive to Atlanta." },
    { q: "What if I live on Fort Campbell or in Oak Grove?", a: "We serve the entire Fort Campbell military community, Oak Grove, and surrounding Christian County area including Cadiz, Eddyville, Princeton, Clarksville (TN), Russellville, Elkton, Pembroke, LaFayette, and Fairview. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Hopkinsville?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Hopkinsville?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and Walmart locations in Hopkinsville, Oak Grove, and Clarksville offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Hopkinsville?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Hopkinsville?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Christian County and Fort Campbell families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Hopkinsville", address: "4400 Fort Campbell Blvd, Hopkinsville, KY 42240", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Hopkinsville", address: "2620 Canton St, Hopkinsville, KY 42240", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Hopkinsville", address: "4000 Fort Campbell Blvd, Hopkinsville, KY 42240", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Hopkinsville", address: "2400 Fort Campbell Blvd, Hopkinsville, KY 42240", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Clarksville", address: "3050 Wilma Rudolph Blvd, Clarksville, TN 37040", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Clarksville", address: "1680 Fort Campbell Blvd, Clarksville, TN 37042", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Oak Grove", address: "1620 Fort Campbell Blvd, Oak Grove, KY 42262", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Hopkinsville", address: "4400 Fort Campbell Blvd, Hopkinsville, KY 42240", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Christian County",
      address: "511 S Main St, Hopkinsville, KY 42240",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Trigg County",
      address: "38 Main St, Cadiz, KY 42211",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Online booking available.",
      featured: false,
    },
    {
      name: "Circuit Court Clerk — Montgomery County (TN)",
      address: "2 Millennium Plaza, Clarksville, TN 37040",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for Tennessee residents in the Fort Campbell area.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Logan County",
      address: "200 W 4th St, Russellville, KY 42276",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for Logan County residents. Walk-ins limited.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BNA",
      name: "Nashville International Airport",
      distance: "70 miles from Hopkinsville",
      address: "1 Terminal Dr, Nashville, TN 37214",
      note: "The primary airport for the Fort Campbell and Christian County area. Major international hub for long-haul flights. Accessible via I-24.",
      flightUrl: "https://www.flynashville.com/",
      flightLabel: "BNA Airport",
      featured: true,
    },
    {
      code: "CKV",
      name: "Clarksville Regional Airport",
      distance: "25 miles from Hopkinsville",
      address: "200 Airport Rd, Clarksville, TN 37042",
      note: "Regional airport for the Clarksville metro area. General aviation and limited commercial service.",
      flightUrl: "https://www.clarksvilleairport.com/",
      flightLabel: "CKV Airport",
      featured: false,
    },
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International",
      distance: "175 miles from Hopkinsville",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "Alternative regional airport for international flights. Accessible via I-65.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "Louisville Airport",
      featured: false,
    },
  ],
};